import {
  BaseState,
  IEventBroadcastReport,
  IPagination,
  IResponse,
  IScheduledEvent,
  PayloadState,
} from '@/types/types';
import { of } from 'rxjs';
import { MutationTree, ActionTree, GetterTree, Module } from 'vuex';
import { RootState, IPaginate } from '../../types/types';
import {
  broadcastReport,
  broadcastReports,
  deleteEvent,
  details,
  event,
  scheduleEvent,
  updateEvent,
} from '../../services/events.service';
import { isNetworkError } from '@/utils/helpers';
import moment from 'moment';
import { map } from 'rxjs/operators';
import router from '../../router/index';

type EventsState = BaseState<IScheduledEvent> & {
  broadcastReports: Array<IEventBroadcastReport>;
  broadcastReport: IEventBroadcastReport | null;
};

const state: EventsState = {
  list: [],
  paginatedList: [],
  details: {
    name: '',
    senderAddress: '',
    message: '',
    state: '',
    executionTimestamp: '',
    selectedGroupsOrContacts: [],
    selectedOption: '',
    payload: null,
  },
  paginate: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 30,
  },
  broadcastReports: [],
  broadcastReport: null,
};

const hydrateScheduledEvents = (
  events: ReadonlyArray<IScheduledEvent | null | undefined> = []
): Array<IScheduledEvent & { color: string; start: string }> => {
  return events.filter(Boolean).map((doc) => {
    const event = doc as IScheduledEvent;
    const eventState = event.state ?? '';

    return {
      ...event,
      color: ['SCHEDULED', 'STARTED'].includes(eventState)
        ? 'primary'
        : 'rgb(1, 193, 76)',
      start: event.executionTimestamp
        ? moment(event.executionTimestamp).format('YYYY-MM-DD hh:mm')
        : '',
    };
  });
};

const mutations: MutationTree<EventsState> = {
  UPDATE_BROADCAST_REPORTS(state, payload: Array<IEventBroadcastReport>) {
    state.broadcastReports = payload;
  },
  UPDATE_BROADCAST_REPORT(state, payload: IEventBroadcastReport | null) {
    state.broadcastReport = payload;
  },
  UPDATE_PAGINATED_LIST(state, payload: Array<IScheduledEvent>) {
    state.paginatedList = payload;
  },
  UPDATE_LIST(state, payload: Array<IScheduledEvent>) {
    state.list = payload;
  },
  UPDATE_DETAILS(state, payload: IScheduledEvent) {
    state.details = payload;
  },
  UPDATE_PAGINATION(
    state,
    payload: Pick<IPaginate, 'total' | 'page' | 'itemsPerPage' | 'pages'>
  ) {
    state.paginate = {
      page: payload.page,
      total: payload.total,
      limit: payload.itemsPerPage,
      pages: payload.pages,
    };
  },
};

const actions: ActionTree<EventsState, RootState> = {
  /** Infobip-style broadcast list with live delivery counts (quiet refresh) */
  async broadcastReports({ commit, dispatch }, payload) {
    try {
      const { query } = payload ?? {};
      const response$ = of<IResponse<IPagination<IEventBroadcastReport>>>(
        await broadcastReports(query ?? '?page=1&size=50')
      ).pipe(map((response) => response.data?.paginateObj?.docs ?? []));
      response$.subscribe((docs) => {
        commit('UPDATE_BROADCAST_REPORTS', docs);
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
        dispatch('snackBarVisibility', true, { root: true });
      }
      // non-network failures stay quiet: the sidebar simply shows plain cards
    }
  },
  async broadcastReport({ commit, dispatch }, id: number | string) {
    try {
      commit('UPDATE_BROADCAST_REPORT', null);
      const response$ = of<IResponse<IEventBroadcastReport>>(
        await broadcastReport(id)
      ).pipe(map((response) => response.data));
      response$.subscribe((doc) => {
        commit('UPDATE_BROADCAST_REPORT', doc);
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, { root: true });
        dispatch('snackBarVisibility', true, { root: true });
      }
    }
  },
  async list({ commit, dispatch }, payload) {
    try {
      dispatch('isPageLoading', true, { root: true });
      dispatch('isDialogLoading', true, { root: true });
      const { query } = payload;
      const response$ = of<IResponse<Array<IScheduledEvent>>>(
        await event(query)
      );
      response$.subscribe((docs: IResponse<Array<IScheduledEvent>>) => {
        commit('UPDATE_LIST', hydrateScheduledEvents(docs.data));
        dispatch('isPageLoading', false, { root: true });
        dispatch('isDialogLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async paginatedList({ commit, dispatch }, payload) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const { query } = payload;
      const response$ = of<IResponse<IPagination<IScheduledEvent>>>(
        await event(query)
      );
      response$.subscribe((data: IResponse<IPagination<IScheduledEvent>>) => {
        const paginateObj = data.data?.paginateObj;
        const {
          page = 1,
          limit = 30,
          pages = 1,
          total = 0,
        } = paginateObj ?? {};
        commit(
          'UPDATE_PAGINATED_LIST',
          hydrateScheduledEvents(paginateObj?.docs)
        );
        commit('UPDATE_PAGINATION', {
          page,
          pages,
          total,
          itemsPerPage: limit,
        });
        dispatch('isPageLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async details({ commit, dispatch }, payload) {
    try {
      dispatch('isPageLoading', true, { root: true });
      dispatch('isDialogLoading', true, { root: true });
      const { id } = payload;
      const response$ = of<IResponse<IScheduledEvent>>(await details(id));
      response$.subscribe((docs: IResponse<IScheduledEvent>) => {
        if (docs.data) {
          commit('UPDATE_DETAILS', docs.data);
        }
        dispatch('isPageLoading', false, { root: true });
        dispatch('isDialogLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async schedule({ dispatch }, payload) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<IResponse<IScheduledEvent>>(
        await scheduleEvent(payload)
      ).pipe(map((data) => data.data));
      response$.subscribe(() => {
        dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
        dispatch('isLoading', false, { root: true });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('snackBarMessage', `SMS Event scheduled successfully`, {
          root: true,
        });
        dispatch('resetFormValues', true, { root: true });
        dispatch('loadEventList', {
          executionTimestamp: payload?.executionTimestamp,
          isPaginated: false,
        });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async updateEvent({ dispatch }, payload) {
    try {
      dispatch('isLoading', true, { root: true });
      const { id, body } = payload;
      const response$ = of<IResponse<IScheduledEvent>>(
        await updateEvent(id, body)
      ).pipe(map((data) => data.data));
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('snackBarMessage', `SMS Event updated successfully`, {
          root: true,
        });
        dispatch('loadEventList', {
          executionTimestamp: payload?.executionTimestamp,
          isPaginated: false,
        });
        dispatch('details', {
          id,
        });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async delete(
    { dispatch },
    payload: { loading: PayloadState; id: number; view: string; date: string }
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<IResponse<IScheduledEvent>>(
        await deleteEvent(payload.id)
      ).pipe(map((data) => data.data));
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('snackBarVisibility', true, { root: true });
        dispatch('snackBarMessage', `SMS Event deleted successfully`, {
          root: true,
        });
        dispatch('updateDialog', payload.loading, { root: true });
        if (payload?.view && payload?.date) {
          dispatch('loadEventList', {
            executionTimestamp: payload?.date,
            isPaginated: false,
          });
          return;
        }
        router.replace({ name: 'account.events.list' });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async loadEventList(
    { dispatch },
    payload: { executionTimestamp: string; isPaginated: boolean }
  ): Promise<void> {
    setTimeout(() => {
      dispatch('list', {
        query: `?activeDate=${moment(payload?.executionTimestamp).format(
          'YYYY-MM-DD'
        )}&isPaginated=${payload.isPaginated ?? false}`,
      });
    }, 1000);
  },
};

const getters: GetterTree<EventsState, RootState> = {
  getAllEvents: (state) => state.list,
  getEventDetails: (state) => state.details,
  getAllPaginatedEvents: (state) => state.paginatedList,
  getPagination: (state) => state.paginate,
  getBroadcastReports: (state) => state.broadcastReports,
  getBroadcastReport: (state) => state.broadcastReport,
};

export const events: Module<EventsState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
